import { useLazyQuery } from '@apollo/client';
import cx from 'classnames';
import { useEffect } from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../app/hooks';
import styles from '../sass/components/ServiceSelector.module.scss';
import AddOnList from './AddOnList';
import RegularServiceList from './RegularServiceList';
import Tab from './Tab';
import TabContainer from './TabContainer';
import { GET_USER_BY_ID } from '../util/gql';
import { useTenant } from './TenantContext';
import { normalizeThemePreset } from '../util/themePreset';
import { isPlanBooking } from '../util/bookingPricing';

interface ServiceSelectorProps {
  type: 'primary' | 'secondary';
  /** Passed to RegularServiceList: the plan offer's "continue" (Aperio). */
  onPlanContinue?: () => void;
}

const primaryService = (showExtrasHint: boolean, t: TFunction) => (
  <div className={styles.customPresentationalOverride}>
    <span>{t('services.selectOne')}</span>
    {showExtrasHint && (
      <p>{t('services.extrasHint')}</p>
    )}
  </div>
);
const serviceSelection = (
  <div>
    <span>Select Requested Services</span>
  </div>
);

export default function ServiceSelector({
  type,
  onPlanContinue,
}: ServiceSelectorProps) {
  const { t } = useTranslation();
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  // Aperio: a plan booking skips the add-ons step, so the "extras in the next
  // step" line only shows once a one-time service is picked. Every other
  // tenant keeps it as it was.
  const themed = normalizeThemePreset(useTenant()?.themePreset) !== 'default';
  const selectedServices = useAppSelector(
    (state) => state.serviceTypes.services,
  );
  const showExtrasHint = !themed
    || (selectedServices.length > 0 && !isPlanBooking(selectedServices));
  const { id: customerId } = useAppSelector((state) => state.customer);

  const [loadUserInformation,
    { data: userInfo, loading, error }] = useLazyQuery(GET_USER_BY_ID);

  useEffect(() => {
    if (currentUser && Object.keys(currentUser).length > 1) {
      const userId = customerId || currentUser.id;
      loadUserInformation({ variables: { userId } });
    }
  }, [customerId, currentUser, loadUserInformation]);

  if (loading) {
    return (
      <div className={styles.services}>
        <div>{t('services.loadingUser')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.services}>
        <div>{t('services.userError')}</div>
      </div>
    );
  }

  const vendorId = userInfo?.getUserById?.vendorId;
  if (vendorId !== 1) {
    return (
      <div className={styles.services}>
        <TabContainer>
          <Tab
            className="container__col-xs-12"
            tab
            selected={false}
            presentational
          >
            {type === 'primary'
              ? primaryService(showExtrasHint, t)
              : t('addOns.selectOptional')}
          </Tab>
        </TabContainer>
        <div
          className={cx(styles.scrollable, {
            'apr-services__list': themed,
          })}
        >
          {type === 'primary'
            ? <RegularServiceList onPlanContinue={onPlanContinue} />
            : <AddOnList />}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.services}>
      <TabContainer>
        <Tab
          className="container__col-xs-12"
          tab
          selected={false}
          presentational
        >
          {serviceSelection}
        </Tab>
      </TabContainer>
      <div className={styles.scrollable}>
        <RegularServiceList />
      </div>
    </div>
  );
}
